/**
 * ModuleMenu - Three-dot dropdown for module actions (About, Settings, etc.).
 * Self-contained: handles its own auth, API calls, and cache invalidation.
 *
 * Also exports archiveModuleWithToast for reuse in the sidebar context menu.
 */

import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useSession } from '@/features/auth/hooks/useSession'
import { resolveWorkspaceId } from '@/lib/workspace-routing'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/lib/query/keys'
import { api } from '@/lib/api'
import { useToast } from '@/app/providers/ToastContext'

// ---------------------------------------------------------------------------
// Rename Module Modal (name only)
// ---------------------------------------------------------------------------

export interface ModuleEditResult {
  label: string
  highlightColor: string | null
  icon: string | null
}

interface ModuleEditModalProps {
  currentLabel: string
  currentColor: string | null
  currentIcon: string | null
  onConfirm: (result: ModuleEditResult) => void
  onCancel: () => void
}

export function ModuleEditModal({ currentLabel, currentColor, currentIcon, onConfirm, onCancel }: ModuleEditModalProps) {
  const [label, setLabel] = useState(currentLabel)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  const handleSubmit = () => {
    const trimmed = label.trim()
    if (!trimmed) { onCancel(); return }
    onConfirm({ label: trimmed, highlightColor: currentColor, icon: currentIcon })
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }}
      onClick={onCancel}
    >
      <div
        className="xv-scroll"
        style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: '10px',
          padding: '24px',
          width: '420px',
          maxWidth: '90vw',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 600, color: '#212529' }}>
          Rename Module
        </h3>

        {/* Module Name */}
        <div style={{ marginBottom: '16px' }}>
          <input
            ref={inputRef}
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSubmit()
              if (e.key === 'Escape') onCancel()
            }}
            style={{
              width: '100%',
              padding: '10px 12px',
              fontSize: '15px',
              border: '1px solid #dee2e6',
              borderRadius: '8px',
              outline: 'none',
              boxSizing: 'border-box',
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = '#4263eb')}
            onBlur={(e) => (e.currentTarget.style.borderColor = '#dee2e6')}
          />
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button
            onClick={onCancel}
            style={{
              padding: '8px 16px',
              fontSize: '13px',
              border: '1px solid #dee2e6',
              borderRadius: '6px',
              background: 'var(--color-surface)',
              color: '#495057',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            style={{
              padding: '8px 16px',
              fontSize: '13px',
              border: 'none',
              borderRadius: '6px',
              background: 'var(--xavior-blue, #4263eb)',
              color: 'white',
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  )
}

/** Legacy wrapper for callers that only need rename */
export function ModuleRenameModal({ currentLabel, onConfirm, onCancel }: { currentLabel: string; onConfirm: (label: string) => void; onCancel: () => void }) {
  return (
    <ModuleEditModal
      currentLabel={currentLabel}
      currentColor={null}
      currentIcon={null}
      onConfirm={(result) => onConfirm(result.label)}
      onCancel={onCancel}
    />
  )
}

// ---------------------------------------------------------------------------
// Archive helper (shared between ModuleMenu and sidebar)
// ---------------------------------------------------------------------------

export async function archiveModuleWithToast(
  token: string | null,
  workspaceId: string,
  moduleId: string,
  showToast: (message: string, type?: 'success' | 'info' | 'error') => void,
  queryClient: { invalidateQueries: (opts: { queryKey: readonly unknown[] }) => Promise<void> },
  conversationId?: string,
  onArchived?: () => void,
) {
  try {
    // Fetch active count BEFORE archiving (avoids cache staleness on the post-archive fetch)
    let countBefore = -1
    try {
      const current = await api.workspaces.listWorkspaceModules(token, workspaceId, { includeArchived: false })
      countBefore = current.length
    } catch {
      // best-effort
    }

    await api.workspaces.updateWorkspaceModule(token, workspaceId, moduleId, { archived: true })

    const remaining = countBefore > 0 ? countBefore - 1 : -1
    const countMessage = remaining >= 0
      ? ` There are now ${remaining} active module${remaining === 1 ? '' : 's'} in this workspace.`
      : ''

    showToast(`Module successfully archived.${countMessage}`, 'success')

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all }),
      queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) }),
      conversationId
        ? queryClient.invalidateQueries({ queryKey: queryKeys.conversations.detail(conversationId) })
        : Promise.resolve(),
    ])
    onArchived?.()
  } catch (err) {
    console.error('[ModuleMenu] Failed to archive module:', err)
    showToast('Failed to archive module.', 'error')
  }
}

// ---------------------------------------------------------------------------
// ModuleMenu component
// ---------------------------------------------------------------------------

interface ModuleMenuProps {
  moduleId: string
  moduleType?: string
  moduleConfig?: Record<string, unknown>
  onArchived?: () => void
  conversationId?: string
  customItems?: Array<{ label: string; icon?: React.ReactNode; onClick: (anchorRect?: DOMRect) => void; keepOpenOnClick?: boolean; disabled?: boolean; toggle?: boolean }>
  extraContent?: React.ReactNode | ((closeMenu: () => void) => React.ReactNode)
  /**
   * Monotonically-increasing signal used to force the menu closed from the
   * parent (e.g. after an async action like "Sync" completes). When this
   * value changes to a non-zero number, the menu closes.
   */
  closeSignal?: number
}

export function ModuleMenu({ moduleId, moduleType, moduleLabel, moduleConfig, onRename, onRenamed, onArchived, conversationId, customItems, extraContent, closeSignal }: ModuleMenuProps) {
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null)
  const { token, selectedContainer } = useSession()
  const workspaceId = resolveWorkspaceId(selectedContainer)
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  const handleToggle = () => {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      const panelWidth = 190
      const left = Math.min(
        Math.max(12, rect.right - panelWidth),
        Math.max(12, window.innerWidth - panelWidth - 12),
      )
      const top = Math.min(
        Math.max(12, rect.bottom + 4),
        Math.max(12, window.innerHeight - 120),
      )
      setMenuPosition({ top, left })
    }
    setOpen(!open)
  }

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  // Force close when the parent increments closeSignal (e.g. after "Sync" finishes)
  const lastCloseSignalRef = useRef<number | undefined>(closeSignal)
  useEffect(() => {
    if (closeSignal === undefined) return
    if (lastCloseSignalRef.current !== closeSignal) {
      lastCloseSignalRef.current = closeSignal
      setOpen(false)
    }
  }, [closeSignal])

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className="module-toolbar-button is-icon-only"
        style={open ? { background: 'var(--color-surface-hover, #f1f3f5)' } : undefined}
        title="Module options"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" />
        </svg>
      </button>

      {open && menuPosition && createPortal(
        <>
          <div className="module-options-backdrop" onClick={() => setOpen(false)} />
          <div
            ref={menuRef}
            className="module-options-menu"
            style={{ top: menuPosition.top, left: menuPosition.left }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            {customItems?.map((item, i) => (
              <button
                key={i}
                onClick={(e) => {
                  if (item.disabled) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  if (!item.keepOpenOnClick) setOpen(false);
                  item.onClick(rect);
                }}
                disabled={item.disabled}
                className="context-menu-item"
                role={item.toggle !== undefined ? 'switch' : undefined}
                aria-checked={item.toggle !== undefined ? item.toggle : undefined}
              >
                {item.icon}
                {item.label}
                {item.toggle !== undefined && (
                  <span
                    aria-hidden="true"
                    style={{
                      marginLeft: 'auto',
                      position: 'relative',
                      width: '34px',
                      height: '18px',
                      flexShrink: 0,
                      background: item.toggle ? 'var(--color-primary)' : 'var(--color-surface-hover)',
                      borderRadius: '9px',
                      transition: 'background 0.2s ease',
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        top: '2px',
                        left: item.toggle ? '18px' : '2px',
                        width: '14px',
                        height: '14px',
                        background: '#fff',
                        borderRadius: '50%',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                        transition: 'left 0.2s ease',
                      }}
                    />
                  </span>
                )}
              </button>
            ))}
          </div>
          {extraContent && (
            <div style={{ borderTop: '1px solid var(--color-border)' }}>
              {typeof extraContent === 'function' ? extraContent(() => setOpen(false)) : extraContent}
            </div>
          )}
        </div>
        </>,
        document.body,
      )}
    </div>
  )
}

// Menu items now use the `context-menu-item` CSS class (shared with Gmail/sidebar menus)

// ---- Keep these exports for other modules that use them ----

export function ModuleArchiveIcon({ onClick, visible }: { onClick: () => void; visible: boolean }) {
  return (
    <button
      onClick={(e) => {
        if (!visible) return
        e.stopPropagation()
        onClick()
      }}
      style={{
        background: 'none',
        border: 'none',
        cursor: visible ? 'pointer' : 'default',
        padding: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        transition: 'opacity 0.2s',
      }}
      title={visible ? 'Archive module' : undefined}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--color-text-muted)' }}>
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </button>
  )
}

export function TrashIcon({ onClick, visible = true }: { onClick: () => void; visible?: boolean }) {
  return (
    <button
      onClick={(e) => {
        if (!visible) return
        e.stopPropagation()
        onClick()
      }}
      style={{
        background: 'none',
        border: 'none',
        cursor: visible ? 'pointer' : 'default',
        padding: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
      }}
      title={visible ? 'Delete' : undefined}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--color-text-faint)' }}>
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </button>
  )
}
